import { observer } from "mobx-react-lite"
import React, { useEffect, useRef } from "react"
import { usePrintStore } from "reusable/printing/PrintStore"
import Box from '@mui/material/Box';
import ReactDOM from 'react-dom'
import PrintContainer from "reusable/printing/PrintContainer";
import { themeConfig } from '@gap/shared/theme';

interface PrintableRegionProps extends React.ComponentPropsWithoutRef<typeof Box> {
   footer?: PrintableFooterContent
   /** default `false` */
   showFooterOnFirstPage?: boolean
}

/** Defines a region of the page that can be printed full-screen */
export default observer(function PrintableRegion({ footer, showFooterOnFirstPage, ...props }: PrintableRegionProps) {
   const printStore = usePrintStore()
   const printableRegionRef = useRef<HTMLElement>()
   const leftContent = formatCssContent(typeof footer === 'string'
      ? footer
      : footer?.left)
   const rightContent = (typeof footer === 'object' && footer.right)
      ? formatCssContent(footer.right)
      : FooterElement.pageIndex

   useEffect(() => {
      const updateContent = () => printStore.contentNode = printableRegionRef.current?.cloneNode(true)
      if (!printStore.contentNode || !printStore.contentLoader.isLoading) {
         setTimeout(() => {
            updateContent()
         }, 0)
      }
   }, [printStore, printableRegionRef, props.children, printStore.contentLoader.isLoading])

   return <>
      <Box {...props} ref={printableRegionRef} sx={{
         ...props.sx || {},
         '& .avoid-page-break': {
            pageBreakInside: 'avoid',
         },
         '@page': {
            size: 'A4 portrait',
            margin: '10mm 10mm 30mm 10mm',
            fontFamily: 'DMSans',
            color: themeConfig.grey['900'],
            '@bottom-left': {
               content: leftContent,
            },
            '@bottom-right': {
               content: rightContent,
            },
         },
         ...showFooterOnFirstPage ? {} : {
            '@page :first': {
               '@bottom-left': {
                  content: '""',
               },
               '@bottom-right': {
                  content: '""',
               },
            }
         }
      }}>
         {props.children}
      </Box>
      {ReactDOM.createPortal(<PrintContainer />, document.body)}
   </>
})

type PrintableFooterContent = string | {
   left?: string | null
   right?: string | null
}

function formatCssContent(content?: string | null) {
   if (!content) return '""'

   return `"${content}"`
}

export enum FooterElement {
   pageIndex = '"Page " counter(page) " of " counter(pages)',
   enSpace = '\\A\\2002 ',
}
